import { useState, useEffect } from "react";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/date-picker";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

interface Cliente {
  id?: string;
  nome: string;
  telefone: string;
  email: string;
  instagram: string;
  data_nascimento: string;
  observacoes: string;
  status?: string;
}

interface ClienteFormProps {
  initialData?: Cliente;
  onSubmit: (data: Cliente) => void;
  onCancel: () => void;
  title: string;
}

export function ClienteForm({ initialData, onSubmit, onCancel, title }: ClienteFormProps) {
  const [formData, setFormData] = useState<Cliente>({
    // traz campos extras do registro (id, status, total_gasto, etc.)
    ...(initialData || {}),
    // mas nenhum campo de texto do form pode ficar null — input controlado quebra.
    nome: initialData?.nome ?? "",
    telefone: initialData?.telefone ?? "",
    email: initialData?.email ?? "",
    instagram: initialData?.instagram ?? "",
    data_nascimento: initialData?.data_nascimento ?? "",
    observacoes: initialData?.observacoes ?? "",
  });

  // Remove @ if user typed it, we handle it in the label/display if needed
  // But the prompt says "com @ automático", usually meaning we show it or prepend it.
  // The previous implementation used: instagram: formData.instagram ? `@${formData.instagram.replace('@', '')}` : ''
  // I will follow that logic for saving, but show the input normally.

  const handleChange = (field: keyof Cliente, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.nome || !formData.telefone) {
      toast.error("Nome e Telefone são obrigatórios!");
      return;
    }

    const formattedData = {
      ...formData,
      instagram: formData.instagram ? `@${formData.instagram.replace(/^@/, '')}` : '',
      // data_nascimento é DATE no banco: string vazia é inválida (erro 22007 do
      // Postgres), precisa ser null. Os demais campos são TEXT e aceitam ''.
      data_nascimento: formData.data_nascimento || null,
    };

    onSubmit(formattedData as Cliente);
  };

  return (
    <div className="bg-card text-foreground p-6 rounded-lg border border-border w-full max-w-2xl mx-auto shadow-sm animate-in zoom-in-95 duration-200">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold">{title}</h2>
        <Button variant="ghost" size="icon" onClick={onCancel} className="text-muted-foreground hover:text-foreground hover:bg-muted">
          <X size={24} />
        </Button>
      </div>

      <form onSubmit={handleSave} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label htmlFor="nome" className="text-muted-foreground text-sm font-medium">Nome Completo</Label>
            <Input 
              id="nome" 
              value={formData.nome} 
              onChange={e => handleChange("nome", e.target.value)} 
              placeholder="Digite o nome completo" 
              className="bg-background border-border focus-visible:border-primary focus-visible:ring-1 focus-visible:ring-primary transition-colors h-11 text-foreground"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="telefone" className="text-muted-foreground text-sm font-medium">Telefone</Label>
            <Input 
              id="telefone" 
              value={formData.telefone} 
              onChange={e => handleChange("telefone", e.target.value)} 
              placeholder="(00) 00000-0000" 
              className="bg-background border-border focus-visible:border-primary focus-visible:ring-1 focus-visible:ring-primary transition-colors h-11 text-foreground"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="text-muted-foreground text-sm font-medium">E-mail</Label>
            <Input 
              id="email" 
              type="email"
              value={formData.email} 
              onChange={e => handleChange("email", e.target.value)} 
              placeholder="exemplo@email.com" 
              className="bg-background border-border focus-visible:border-primary focus-visible:ring-1 focus-visible:ring-primary transition-colors h-11 text-foreground"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="instagram" className="text-muted-foreground text-sm font-medium">Instagram</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">@</span>
              <Input 
                id="instagram" 
                value={(formData.instagram || '').replace(/^@/, '')}
                onChange={e => handleChange("instagram", e.target.value)} 
                placeholder="usuario" 
                className="bg-background border-border focus-visible:border-primary focus-visible:ring-1 focus-visible:ring-primary transition-colors h-11 pl-8 text-foreground"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="data_nascimento" className="text-muted-foreground text-sm font-medium">Data de Nascimento</Label>
            <DatePicker
              id="data_nascimento"
              value={formData.data_nascimento}
              onChange={v => handleChange("data_nascimento", v)}
              className="h-11"
              comSeletorDeAno
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="observacoes" className="text-muted-foreground text-sm font-medium">Observações</Label>
          <Textarea 
            id="observacoes" 
            value={formData.observacoes} 
            onChange={e => handleChange("observacoes", e.target.value)} 
            placeholder="Alergias, estilos favoritos, detalhes importantes..." 
            className="bg-background border-border focus-visible:border-primary focus-visible:ring-1 focus-visible:ring-primary transition-colors min-h-[120px] resize-none text-foreground"
          />
        </div>

        <div className="flex gap-3 pt-2">
          <Button 
            type="submit" 
            className="flex-1 bg-primary hover:bg-primary/90 text-white font-semibold h-12 transition-all"
          >
            Salvar Cliente
          </Button>
          <Button 
            type="button" 
            variant="outline"
            onClick={onCancel}
            className="px-8 border-border bg-transparent text-muted-foreground hover:bg-muted hover:text-foreground h-12 transition-all"
          >
            Cancelar
          </Button>
        </div>
      </form>
    </div>
  );
}
